import { Dialog, DialogTitle, DialogContent, Button } from "@mui/material";
import MUIDataTable from "mui-datatables";
import React, { useState, useEffect } from "react";

import LoadingOverlay from "./components/LoadingOverlay";
import { getCommonCellProps, getCommonHeaderProps } from "./components/TableColumnConfig";
import { fetchData } from "./dataHandler";

const ActiveImplementationsTab = (props) => {
    const [implementationsTableData, setImplementationsTableData] = useState([]);
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [updateStatus, setUpdateStatus] = useState({ message: "", isError: false });
    const [showStatus, setShowStatus] = useState(false);

    const [open, setOpen] = useState(false);
    const [selectedHtml, setSelectedHtml] = useState("");

    const handleOpen = (html) => {
        setSelectedHtml(html);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    // Function to convert decimal hours to hours and minutes format
    const formatHoursToHoursMinutes = (decimalHours) => {
        if (
            decimalHours === "-" ||
            decimalHours === null ||
            decimalHours === undefined ||
            decimalHours === ""
        ) {
            return "-";
        }

        // Handle zero hours case
        if (decimalHours === 0) {
            return "0m";
        }

        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);

        return `${hours}h ${minutes}m`;
    };

    useEffect(() => {
        if (props.data) {
            // Check if data is in the expected format with activeImplementations array
            let implementationsArray = props.data.activeImplementations || [];

            // If no implementations found, set empty array and return
            if (implementationsArray.length === 0) {
                setImplementationsTableData([]);
                return;
            }

            // Process the implementations array
            const processedData = implementationsArray.map((item) => {
                // Create a new object to avoid mutating the original
                const processedItem = { ...item };

                // Replace undefined or null values
                Object.keys(processedItem).forEach((key) => {
                    if (processedItem[key] === undefined || processedItem[key] === null) {
                        processedItem[key] = "-";
                    }
                });

                return processedItem;
            });

            setImplementationsTableData(processedData);
        }
    }, [props.data]);

    // Handle editing a row
    const handleEditClick = (rowIndex) => {
        setEditingRow(rowIndex);
        setEditFormData({ ...implementationsTableData[rowIndex] });
    };

    const handleSaveClick = async (rowIndex) => {
        try {
            const implementationId = implementationsTableData[rowIndex].id;

            // Clear any previous status messages
            setUpdateStatus({ message: "Saving changes...", isError: false });
            setShowStatus(true);

            // Make API call to update the implementation
            const response = await fetchData(
                "updateimplementation",
                { id: implementationId },
                "POST",
                editFormData
            );

            if (response.values && response.values.success) {
                // Update local data
                const updatedData = [...implementationsTableData];
                updatedData[rowIndex] = { ...editFormData };
                setImplementationsTableData(updatedData);

                // Show success message
                setUpdateStatus({
                    message: "Implementation updated successfully!",
                    isError: false,
                });
            } else {
                // Show error message
                setUpdateStatus({
                    message: response.values?.message || "Failed to update implementation",
                    isError: true,
                });
            }
        } catch (error) {
            console.error("Error updating implementation:", error);
            setUpdateStatus({
                message: `Error updating implementation: ${error.message}`,
                isError: true,
            });
        }

        // Hide status after a delay
        setTimeout(() => {
            setShowStatus(false);
        }, 3000);

        // Exit edit mode
        setEditingRow(null);
    };

    const handleInputChange = (e, field) => {
        setEditFormData({
            ...editFormData,
            [field]: e.target.value,
        });
    };

    const handleDateChange = (e, field) => {
        let value = e.target.value;
        setEditFormData({
            ...editFormData,
            [field]: value,
        });
    };

    // Render edit/save/cancel actions
    const renderActions = (tableMeta) => {
        const rowIndex = tableMeta.rowIndex;

        if (editingRow === rowIndex) {
            return (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleSaveClick(rowIndex)}
                        className="text-green-600 hover:text-green-800"
                        title="Save"
                    >
                        <i className="fas fa-save"></i>
                    </button>
                    <button
                        onClick={() => setEditingRow(null)}
                        className="text-red-600 hover:text-red-800"
                        title="Cancel"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            );
        }

        return (
            <button
                onClick={() => handleEditClick(rowIndex)}
                className="text-blue-600 hover:text-blue-800"
                title="Edit"
            >
                <i className="fas fa-pencil-alt"></i>
            </button>
        );
    };

    // Render editable cell or display value
    const renderEditableCell = (value, field, tableMeta) => {
        const rowIndex = tableMeta.rowIndex;

        if (editingRow === rowIndex) {
            return (
                <input
                    type="text"
                    value={editFormData[field] || ""}
                    onChange={(e) => handleInputChange(e, field)}
                    className="w-full rounded border border-gray-300 p-1"
                />
            );
        }

        return value;
    };

    // Render editable date cell
    const renderEditableDateCell = (value, field, tableMeta) => {
        const rowIndex = tableMeta.rowIndex;

        if (editingRow === rowIndex) {
            let dateValue = "";
            if (value && value !== "-") {
                const date = new Date(value);
                dateValue = date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
            }

            return (
                <input
                    type="date"
                    value={
                        editFormData[field]?.includes("T")
                            ? editFormData[field].split("T")[0]
                            : editFormData[field] || ""
                    }
                    onChange={(e) => handleDateChange(e, field)}
                    className="w-full rounded border border-gray-300 p-1"
                />
            );
        }

        if (value && value !== "-") {
            const date = new Date(value);
            return date.toLocaleDateString("en-US");
        }

        return value;
    };

    const columns = [
        {
            name: "customer",
            label: "Customer",
            options: {
                setCellProps: () => getCommonCellProps(),
                setCellHeaderProps: () => getCommonHeaderProps(),
            },
        },
        {
            name: "product",
            label: "Product",
            options: {
                setCellProps: () => getCommonCellProps(),
                setCellHeaderProps: () => getCommonHeaderProps(),
            },
        },
        {
            name: "status",
            label: "Status",
            options: {
                setCellProps: () => getCommonCellProps("100px"),
                setCellHeaderProps: () => getCommonHeaderProps("100px"),
                filterType: "dropdown", // or 'checkbox' if you want multiple selections
                filterList: ["In-progress"],
            },
        },
        {
            name: "owner",
            label: "Owner",
            options: {
                setCellProps: () => getCommonCellProps(),
                setCellHeaderProps: () => getCommonHeaderProps(),
            },
        },
        {
            name: "kick_off_date",
            label: "Kick Off Date",
            options: {
                setCellProps: () => getCommonCellProps("100px"),
                setCellHeaderProps: () => getCommonHeaderProps("100px"),
            },
        },
        {
            name: "go_live_date",
            label: "Go Live Date",
            options: {
                setCellProps: () => getCommonCellProps("100px"),
                setCellHeaderProps: () => getCommonHeaderProps("100px"),
            },
        },
        {
            name: "closure_date",
            label: "Closure Date",
            options: {
                setCellProps: () => getCommonCellProps("100px"),
                setCellHeaderProps: () => getCommonHeaderProps("100px"),
            },
        },
        {
            name: "budgeted_hours",
            label: "Budgeted Hours",
            options: {
                setCellProps: () => getCommonCellProps("100px"),
                setCellHeaderProps: () => getCommonHeaderProps("100px"),
                customBodyRender: (value) => formatHoursToHoursMinutes(value),
            },
        },
        {
            name: "tracked_hours",
            label: "Tracked Hours",
            options: {
                setCellProps: () => getCommonCellProps("100px"),
                setCellHeaderProps: () => getCommonHeaderProps("100px"),
                customBodyRender: (value) => formatHoursToHoursMinutes(value),
            },
        },
        {
            name: "implementation_notes",
            label: "Implementation Notes",
            options: {
                customBodyRender: (value) =>
                    value != "-" ? (
                        <Button variant="text" color="primary" onClick={() => handleOpen(value)}>
                            View
                        </Button>
                    ) : (
                        "-"
                    ),
                filter: false,
            },
        },
    ];

    const options = {
        responsive: "standard",
        selectableRows: "none",
        searchAlwaysOpen: true,
        storageKey: "activeImplementationsTable",
        filter: true,
        sort: true,
        rowsPerPage: 250,
        rowsPerPageOptions: [50, 100, 250, 500, 1000],
        print: false,
        download: false,
        search: true,
        columnOrder: null,
        fixedHeader: false,
    };

    return (
        <div className="flex w-full flex-col">
            {/* Status message */}
            {showStatus && (
                <div
                    className={`mb-4 rounded-md p-3 ${
                        updateStatus.isError
                            ? "border border-red-400 bg-red-100 text-red-700"
                            : "border border-green-400 bg-green-100 text-green-700"
                    }`}
                >
                    {updateStatus.message}
                </div>
            )}

            {implementationsTableData.length > 0 ? (
                <>
                    <MUIDataTable
                        data={implementationsTableData}
                        columns={columns}
                        options={options}
                        className="ngShadow"
                    />

                    {/* Popup Dialog */}
                    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                        <DialogTitle>Implementation Notes</DialogTitle>
                        <DialogContent dividers>
                            <div
                                className="text-[15px] leading-relaxed text-gray-800 [&_li]:mb-1 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6"
                                dangerouslySetInnerHTML={{
                                    __html: selectedHtml,
                                }}
                            />
                        </DialogContent>
                    </Dialog>
                </>
            ) : props.data?.activeImplementations && props.data.activeImplementations.length > 0 ? (
                <LoadingOverlay text="Loading implementations..." />
            ) : (
                <h3 className="px-2 pt-8 text-center">No implementations found.</h3>
            )}
        </div>
    );
};

export default ActiveImplementationsTab;
